import { ref, computed } from 'vue'
import { useApolloClient } from '@vue/apollo-composable'

import type { KarteUser, KarteID, DiagnosisSummary } from '@/types/app'
import {
  DiagnosisByKarteDocument,
  FetchKarteOnUseKarteUserDetailDocument,
} from './useKarteUserDetail.generated.ts'
import type {
  FetchKarteOnUseKarteUserDetailQuery,
  DiagnosisByKarteQuery,
} from './useKarteUserDetail.generated'
import { parseDiagnosisByKarteResponseToDiagnosisSummary } from '@/apis/repositories/parsers/diagnosis.ts'
import { parseDiagnosisByKarteResponseToUser } from '@/apis/repositories/parsers/karte.ts'

export const useKarteUserDetail = (
  onDetailLoadError: (error: Error) => void
) => {
  const currentUserDiagnosisSummary = ref<Maybe<DiagnosisSummary>>(undefined)
  const currentUser = ref<Maybe<KarteUser>>(undefined)
  const karte = ref<FetchKarteOnUseKarteUserDetailQuery['user']['karte']>(null)

  const isDetailFetching = ref(false)
  const isDetailReady = computed(
    () =>
      currentUserDiagnosisSummary.value !== undefined && !isDetailFetching.value
  )

  const apollo = useApolloClient()
  const fetchUserDetail = async (karteId: KarteID): Promise<void> => {
    try {
      isDetailFetching.value = true

      const { data: diagnosisSummary } =
        await apollo.client.query<DiagnosisByKarteQuery>({
          query: DiagnosisByKarteDocument,
          variables: {
            karteId: karteId.toString(),
          },
        })

      currentUserDiagnosisSummary.value =
        parseDiagnosisByKarteResponseToDiagnosisSummary(diagnosisSummary)
      currentUser.value = parseDiagnosisByKarteResponseToUser(diagnosisSummary)

      const { data } =
        await apollo.client.query<FetchKarteOnUseKarteUserDetailQuery>({
          query: FetchKarteOnUseKarteUserDetailDocument,
          variables: {
            id: currentUser.value.id.toString(),
          },
        })
      karte.value = data.user.karte
    } catch (error) {
      onDetailLoadError(error as Error)
    } finally {
      isDetailFetching.value = false
    }
  }

  // FIXME:
  // karteはgqlから取得したもの。
  // KarteUserDetailViewで、KarteUserConcernが求める型がgqlから取得したものなので、
  // ここで返すようにする。
  // KarteUserConcernはcurrentUser.karteを渡さない
  // ここらへんの整理はこのあとやる
  return {
    currentUser,
    currentUserDiagnosisSummary,
    karte,
    fetchUserDetail,
    isDetailReady,
  }
}
